/* eslint-disable @typescript-eslint/ban-ts-comment */
import {
  useMemo,
  useState,
  useCallback,
  KeyboardEvent,
  KeyboardEventHandler,
} from 'react'
import { Stack, styled } from '@mui/material'
import { AquaList as Aqua, registerAquaManager } from '@weblite/aqua'
// @ts-ignore
import hostConf from '$config/frontend'
import {
  shouldLoadMessages,
  loadNextMessagesOfRoom,
  loadPreviousMessagesOfRoom,
  editLastMessage,
  useOpenedRoomId,
  useOpenedRoomPermissions,
} from '$logic/opened-room'
import { useRoomAttribute } from '$logic/room/react-hook'
import { closeRoom } from '$logic/router'
import { isPhoneOrTablet } from '$func/environmentInfo'
import { roomSearchApi } from '$comp/_app/components/chat/room'
import { editorAPI } from '$comp/_app/components/chat/components/footer/components/send-field/components/text-editor'
import { saveRoomLastScroll } from '$logic/room-last-scroll'
import { useRoomLastMessage } from '$logic/room-last-message/react-hook'
import { pinContentApi } from '$comp/_app/components/chat/components/pin/components'
import {
  DateSeparator,
  UnseenSeparator,
  MessageListEmpty,
  MessageListLoading,
  MessageWrapper,
  ScrollToBottom,
  ScrollToMention,
  ChannelVitrineButton,
} from './components'
import {
  MessageListItem,
  IDateSeparator,
  useOpenedRoomMessageList,
} from './react-hooks'
import { JoinedChannelEvent } from './components/joined-channel-event/joined-channel-event'

export const ROOM_MESSAGE_LIST_AQUA_MANAGER_ID = 'room-message-list'
registerAquaManager(ROOM_MESSAGE_LIST_AQUA_MANAGER_ID)

const Container = styled('div')`
  display: flex;
  flex-direction: column;
  position: relative;
  height: 100%;
  justify-content: flex-end;
  padding: 0;

  & > ul {
    direction: rtl;
  }
`

const LoadingContainer = styled('div')`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
`

const UP_KEY = 38
const onKeyDown: KeyboardEventHandler<HTMLElement> = e => {
  if (e.key === 'Enter' && !e.shiftKey && !isPhoneOrTablet) {
    e.preventDefault()
    editorAPI.focus()
    return
  }

  if (e.key === 'Escape') {
    e.preventDefault()
    closeRoom()
    return
  }

  if (e.keyCode === UP_KEY && !editorAPI.getText()) {
    e.preventDefault()
    editLastMessage()
    return
  }

  if ((e.ctrlKey || e.metaKey) && e.key === 'f') {
    e.preventDefault()
    roomSearchApi.handleSearch('')
  }
}

export function MessageList({
  toggleSelection,
  vitrineButtonOffset,
}: {
  toggleSelection: (selected: boolean) => (iid: number) => unknown
  vitrineButtonOffset?: number
}) {
  const [loadingNextMessages, setLoadingNextMessages] = useState(false)
  const [loadingPreviousMessages, setLoadingPreviousMessages] = useState(false)

  const [shouldHideScrollButton, setShouldHideScrollButton] = useState(true)
  const { loading: messagesLoading, messageList } = useOpenedRoomMessageList()

  const roomId = useOpenedRoomId() || ''
  const roomType = useRoomAttribute('type', roomId)
  const isChannel = roomType === 'CHANNEL'
  const isGroup = roomType === 'GROUP'

  const lastMessage = useRoomLastMessage(roomId)

  // TODO: check performance issues
  const {
    openedRoomPermissions,
    loading: permissionLoading,
  } = useOpenedRoomPermissions()

  const { sendMessage, sendMedia, sendWapp } = openedRoomPermissions || {}
  const hasPermissionToSendMessage = !!(sendMessage || sendMedia || sendWapp)

  const renderer = useCallback(
    (message: MessageListItem) => {
      const {
        type,
        posInBlock,
        showSender,
        needVisibility,
        ...messageRest
      } = message

      if (type === 'INLINE_LOADING')
        return (
          <MessageListLoading
            key="INLINE_LOADING"
            isInline
            offset={vitrineButtonOffset}
          />
        )

      if (type === 'UNSEEN_SEPARATOR')
        return (
          <UnseenSeparator key="UNSEEN_SEPARATOR" posInBlock={posInBlock} />
        )

      if (type === 'DATE_SEPARATOR')
        return (
          <DateSeparator
            date={(message as IDateSeparator).date}
            key={(message as IDateSeparator).date}
            posInBlock={posInBlock}
          />
        )

      if (type === 'JOINED_CHANNEL_EVENT')
        return <JoinedChannelEvent posInBlock={posInBlock} />

      return (
        <MessageWrapper
          // @ts-ignore
          key={messageRest.id}
          // @ts-ignore
          message={{ type, ...messageRest }}
          posInBlock={posInBlock}
          showSender={showSender}
          needVisibility={needVisibility}
          toggleSelection={toggleSelection}
          roomType={roomType}
          hasPermissionToSendMessages={hasPermissionToSendMessage}
        />
      )
    },
    [
      roomType,
      hasPermissionToSendMessage,
      vitrineButtonOffset,
      toggleSelection,
    ],
  )

  const loadPreviousMessages = useCallback(async () => {
    if (loadingPreviousMessages || !(await shouldLoadMessages('PREVIOUS')))
      return

    setLoadingPreviousMessages(true)
    if (!loadingPreviousMessages)
      loadPreviousMessagesOfRoom().finally(() =>
        setLoadingPreviousMessages(false),
      )
  }, [loadingPreviousMessages])

  const loadNextMessages = useCallback(async () => {
    if (loadingNextMessages || !(await shouldLoadMessages('NEXT'))) return

    setLoadingNextMessages(true)
    if (!loadingNextMessages)
      loadNextMessagesOfRoom().finally(() => setLoadingNextMessages(false))
  }, [loadingNextMessages])

  const messagesWithInlineLoading = useMemo(() => {
    const inlineLoadingItem = { type: 'INLINE_LOADING' }
    const loading = loadingNextMessages || loadingPreviousMessages
    return [loading && inlineLoadingItem, ...messageList].filter(Boolean)
    // return messageList.filter(Boolean)
  }, [messageList, loadingNextMessages, loadingPreviousMessages])

  const handleSaveTopElementId = useCallback(
    (id?: string, iid?: number, isAtBottom?: boolean) => {
      if (roomId)
        saveRoomLastScroll(roomId, {
          topViewPortMessageId: id,
          topViewPortMessageIid: iid,
          bottom: Boolean(isAtBottom),
        })
    },
    [roomId],
  )

  const AquaList = useMemo(
    () => (
      <Aqua
        // idKey="_id"
        list={messagesWithInlineLoading}
        renderer={renderer}
        onReachTop={loadPreviousMessages}
        onReachBottom={loadNextMessages}
        setShouldHideScrollButton={setShouldHideScrollButton}
        onKeyDown={(a: KeyboardEvent<HTMLElement>) => {
          if (roomId) onKeyDown(a)
        }}
        onChangeTopElement={handleSaveTopElementId}
        lastMessageId={lastMessage?.id}
        managerId={ROOM_MESSAGE_LIST_AQUA_MANAGER_ID}
        onUserScroll={direction => {
          pinContentApi.onScroll(direction)
        }}
      />
    ),
    [
      messagesWithInlineLoading,
      lastMessage,
      loadPreviousMessages,
      loadNextMessages,
      renderer,
      handleSaveTopElementId,
      roomId,
    ],
  )

  return (
    <Container>
      {messagesLoading || permissionLoading ? (
        <LoadingContainer>
          <MessageListLoading />
        </LoadingContainer>
      ) : messageList?.length ? (
        <>
          {AquaList}

          {isChannel && hostConf.HAS_CHANNEL_VITRINE && (
            <ChannelVitrineButton
              roomId={roomId}
              expanded={shouldHideScrollButton}
              offset={vitrineButtonOffset}
            />
          )}
          <Stack
            sx={{ position: 'absolute', bottom: '12px', right: '12px' }}
            gap="16px"
            zIndex={1}
          >
            {isGroup && <ScrollToMention />}
            <ScrollToBottom shouldHide={shouldHideScrollButton} />
          </Stack>
        </>
      ) : (
        <MessageListEmpty />
      )}
    </Container>
  )
}
